import { useRouter } from 'next/navigation'

import { useTenant } from '@rewardops/auth'
import {
  invertHex,
  processUrl,
  type ContentfulAdBanner,
} from '@rewardops/utils'

import { useFilterBySegmentationTags } from '../hooks/use-filter-by-segmentation-tags'
import AdButton from './adButton'
import ContentfulImage from './contentfulImage'

type Props = {
  content: ContentfulAdBanner
}

function AdBanner(props: Props) {
  const { content } = props
  const tenant = useTenant()
  const backgroundColor = content.color || 'FFFFFF'
  const textColor = tenant?.adBannerTextColor ?? invertHex(backgroundColor)
  const { rightAlignedImage } = content
  const { linkText, url: productUrl } = content
  const router = useRouter()
  const isBannerSupportedForSegment = useFilterBySegmentationTags({
    bannerContent: content,
  })

  const onClick = () => {
    const { isAbsolute, normalizedUrl } = processUrl(productUrl || '')

    if (!isAbsolute) {
      router.push(productUrl || '/')
      return
    }

    window.open(normalizedUrl, '_blank', 'noopener,noreferrer')
  }

  function getAdImage() {
    return (
      <ContentfulImage
        content={content.heroImage}
        label={content.heroImage?.title ?? 'Ad Banner Image'}
        className="col-span-2 md:col-span-1"
        maxHeight="22rem"
      />
    )
  }

  if (!Boolean(isBannerSupportedForSegment)) {
    return null
  }

  return (
    <div
      className="col-span-2 flex justify-center py-20"
      style={{ background: `#${backgroundColor}`, color: textColor }}
    >
      <div className="container grid grid-cols-2 md:px-5 ">
        {!rightAlignedImage ? getAdImage() : undefined}
        <div className="order-last col-span-2 grid content-center justify-center p-10 pr-0 text-center md:-order-none md:col-span-1">
          <ContentfulImage
            content={content.logoImage}
            label={content.headerText ?? 'Ad Banner'}
            className="h-24 w-auto"
            height="inherit"
            width="inherit"
          />
          <div className="pt-5 text-5xl font-medium tracking-tighter">
            {content.headerText}
          </div>
          {content.subText ? (
            <div className="pt-2 text-lg">{content.subText}</div>
          ) : undefined}
          {linkText && productUrl ? (
            <AdButton
              id={content.id}
              onClick={onClick}
              aria-label={`${linkText}: ${content.headerText}`}
            >
              {linkText}
            </AdButton>
          ) : undefined}
        </div>
        {rightAlignedImage ? getAdImage() : undefined}
      </div>
    </div>
  )
}

export default AdBanner
